import { useState, useEffect, useMemo } from 'react';

import { useAuthToken } from '../../../../hooks/useAuthToken';
import { useResponsive } from '../../../../hooks/useResponsive';
import { edgeFunctionsApi, debuggingApi } from '../../../../lib/api/supabase';
import { Button } from '../../../../components/ui/button';
import { cn } from '../../../../lib/utils';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../components/ui/select';
import { Terminal, RefreshCw, ChevronRight, ChevronDown, Copy } from 'lucide-react';
import toast from 'react-hot-toast';

interface LogsTabProps {
  projectId: string;
  initialFunctionSlug?: string;
}

type LogSource = 'edge-functions' | 'auth' | 'storage' | 'api' | 'postgres';
type TimeInterval = '15min' | '1hr' | '3hr' | '1day';
type EdgeFunctionView = 'console' | 'invocations';

interface LogEntry {
  id: string;
  timestamp: Date;
  level: string;
  message: string;
  functionName?: string;
  source: LogSource;
  metadata?: unknown[];
}

interface DeployedFunction {
  id: string;
  slug: string;
  name: string;
}

const LOG_SOURCES: { value: LogSource; label: string }[] = [
  { value: 'edge-functions', label: 'Edge Functions' },
  { value: 'auth', label: 'Authentication' },
  { value: 'storage', label: 'Storage' },
  { value: 'api', label: 'API Gateway' },
  { value: 'postgres', label: 'Postgres' },
];

const TIME_INTERVALS: { value: TimeInterval; label: string }[] = [
  { value: '15min', label: 'Last 15 minutes' },
  { value: '1hr', label: 'Last hour' },
  { value: '3hr', label: 'Last 3 hours' },
  { value: '1day', label: 'Last 24 hours' },
];

export function LogsTab({ projectId, initialFunctionSlug }: LogsTabProps) {
  const { token } = useAuthToken();
  const { isMobile } = useResponsive();
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [source, setSource] = useState<LogSource>('edge-functions');
  const [interval, setInterval] = useState<TimeInterval>('1hr');
  const [expandedLogs, setExpandedLogs] = useState<Set<string>>(new Set());
  const [edgeFunctionView, setEdgeFunctionView] = useState<EdgeFunctionView>('invocations');

  // Invocations state
  interface Invocation {
    id: string;
    timestamp: Date;
    functionName: string;
    method: string;
    status: number;
    duration: number;
    path?: string;
    metadata?: any;
  }
  const [invocations, setInvocations] = useState<Invocation[]>([]);

  // Edge functions state
  const [deployedFunctions, setDeployedFunctions] = useState<DeployedFunction[]>([]);
  const [selectedFunction, setSelectedFunction] = useState<string>(initialFunctionSlug || 'all');
  const [functionsLoaded, setFunctionsLoaded] = useState(false);

  // Create UUID → slug mapping for display
  const functionIdToSlug = useMemo(() => {
    const map = new Map<string, string>();
    deployedFunctions.forEach((fn) => {
      map.set(fn.id, fn.slug);
    });
    return map;
  }, [deployedFunctions]);

  // Load deployed functions list once when switching to edge-functions source
  useEffect(() => {
    if (token && source === 'edge-functions' && !functionsLoaded) {
      loadDeployedFunctions();
    }
  }, [token, source, functionsLoaded]);

  const loadDeployedFunctions = async () => {
    if (!token) return;
    try {
      const response = await edgeFunctionsApi.listFunctions(token, projectId);
      setDeployedFunctions(response.functions || []);
      setFunctionsLoaded(true);
    } catch (error) {
      console.error('Failed to load deployed functions:', error);
      setFunctionsLoaded(true); // Mark as loaded even on error to avoid retries
    }
  };

  const toggleLogExpanded = (logId: string) => {
    setExpandedLogs((prev) => {
      const next = new Set(prev);
      if (next.has(logId)) {
        next.delete(logId);
      } else {
        next.add(logId);
      }
      return next;
    });
  };

  const copyLog = (log: LogEntry, e: React.MouseEvent) => {
    e.stopPropagation();
    const logText = `[${log.timestamp.toISOString()}] [${log.level.toUpperCase()}]${log.functionName ? ` [${log.functionName}]` : ''} ${log.message}${log.metadata && log.metadata.length > 0 ? '\n' + JSON.stringify(log.metadata, null, 2) : ''}`;
    navigator.clipboard.writeText(logText);
    toast.success('Log copied to clipboard');
  };

  useEffect(() => {
    if (token) {
      loadLogs(initialLoading);
    }
  }, [projectId, token, source, interval, selectedFunction, edgeFunctionView]);

  const loadLogs = async (isInitial = false) => {
    if (!token) return;
    setLoading(true);
    try {
      let formattedLogs: LogEntry[] = [];

      if (source === 'edge-functions') {
        // Find the function ID if filtering by a specific function
        const functionId = selectedFunction !== 'all'
          ? deployedFunctions.find((fn) => fn.slug === selectedFunction)?.id
          : undefined;

        if (edgeFunctionView === 'invocations') {
          // Fetch invocations
          const response = await edgeFunctionsApi.getFunctionInvocations(token, projectId, {
            functionName: selectedFunction !== 'all' ? selectedFunction : undefined,
            interval,
            limit: 50,
          });

          setInvocations(response.invocations.map((inv) => ({
            id: inv.id,
            timestamp: new Date(inv.timestamp),
            functionName: inv.functionName,
            method: inv.method,
            status: inv.status,
            duration: inv.duration,
            path: inv.path,
            metadata: inv.metadata,
          })));
          setLogs([]);
        } else {
          // Fetch console logs
          const response = await edgeFunctionsApi.getFunctionLogs(token, projectId, {
            level: undefined,
            interval,
            limit: 50,
            functionId,
          });

          formattedLogs = response.logs.map((log) => ({
            id: log.id,
            timestamp: new Date(log.timestamp),
            level: log.level,
            message: log.message,
            functionName: log.functionName
              ? (functionIdToSlug.get(log.functionName) || log.functionName)
              : undefined,
            source: 'edge-functions' as LogSource,
            metadata: log.metadata,
          }));
          setInvocations([]);
        }
      } else if (source === 'auth' || source === 'storage' || source === 'api' || source === 'postgres') {
        const response = source === 'postgres'
          ? await debuggingApi.getLogs(token, projectId, source, 50)
          : await debuggingApi.getLogs(token, projectId, source, 50, interval);

        formattedLogs = response.data.map((log, index) => {
          let level = 'info';
          if (source === 'api') {
            // API Gateway: derive level from HTTP status code (position 1)
            const statusStr = log.event_message.split(' | ')[1]?.trim();
            const status = parseInt(statusStr || '200');
            if (status >= 500) level = 'error';
            else if (status >= 400) level = 'warn';
          } else if (source === 'storage') {
            // Storage: derive level from HTTP status code (position 2)
            const statusStr = log.event_message.split(' | ')[2]?.trim();
            const status = parseInt(statusStr || '200');
            if (status >= 500) level = 'error';
            else if (status >= 400) level = 'warn';
          } else if (source === 'postgres') {
            // Postgres: level from metadata[0].parsed[0].error_severity
            const parsed = (log.metadata as any[])?.[0]?.parsed;
            const severity = Array.isArray(parsed) ? parsed[0]?.error_severity : undefined;
            if (typeof severity === 'string') {
              const s = severity.toLowerCase();
              if (s === 'error' || s === 'fatal' || s === 'panic') level = 'error';
              else if (s === 'warning') level = 'warn';
              else level = s;
            }
          } else {
            // Auth: event_message is JSON, extract level from it
            try {
              const parsed = JSON.parse(log.event_message);
              if (parsed.level) level = parsed.level;
            } catch {
              // Not JSON, keep default
            }
          }
          return {
            id: log.id || `${source}-${index}`,
            timestamp: new Date(log.timestamp),
            level,
            message: log.event_message,
            source: source as LogSource,
            metadata: log.metadata,
          };
        });
      }

      setLogs(formattedLogs);
      // Keep all logs collapsed by default
      setExpandedLogs(new Set());
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      toast.error(`Failed to load logs: ${errorMessage}`);
    } finally {
      setLoading(false);
      if (isInitial) {
        setInitialLoading(false);
      }
    }
  };

  const getLevelBadgeClass = (logLevel: string) => {
    switch (logLevel.toLowerCase()) {
      case 'error':
      case 'fatal':
      case 'panic':
        return 'bg-red-500/10 text-red-600';
      case 'warn':
      case 'warning':
        return 'bg-amber-500/10 text-amber-600';
      case 'info':
        return 'bg-blue-500/10 text-blue-600';
      case 'debug':
        return 'bg-purple-500/10 text-purple-600';
      default:
        return 'bg-blue-500/10 text-blue-600';
    }
  };

  const formatTimestamp = (date: Date) => {
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
  };

  const formatDate = (date: Date) => {
    const today = new Date();
    const isToday = date.toDateString() === today.toDateString();

    if (isToday) {
      return 'Today';
    }

    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  };

  // Reset function filter when switching sources
  const handleSourceChange = (newSource: LogSource) => {
    setSource(newSource);
    if (newSource !== 'edge-functions') {
      setSelectedFunction('all');
    }
  };

  if (initialLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center gap-2 text-muted-foreground">
          <div className="w-4 h-4 border-2 border-muted-foreground/30 border-t-muted-foreground rounded-full animate-spin" />
          <span>Loading logs...</span>
        </div>
      </div>
    );
  }

  return (
    <div className={cn("space-y-4 min-w-0", isMobile && "space-y-3")}>
      {/* Header - compact on mobile */}
      {isMobile ? (
        <div className="flex items-center justify-between">
          <h3 className="text-base font-semibold">Logs</h3>
          <button
            onClick={() => loadLogs()}
            className="p-2 hover:bg-muted rounded-md transition-colors"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      ) : (
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold">Logs</h3>
            <p className="text-sm text-muted-foreground mt-0.5">
              View logs from your OptiDev Cloud services
            </p>
          </div>
          <Button onClick={() => loadLogs()} variant="outline" size="sm">
            <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      )}

      {/* Filters - single scrollable row on mobile */}
      {isMobile ? (
        <div className="flex gap-2 overflow-x-auto pb-1 -mx-4 px-4 scrollbar-hide">
          <Select value={source} onValueChange={(v) => handleSourceChange(v as LogSource)}>
            <SelectTrigger className="h-8 text-xs w-auto min-w-fit px-2.5 flex-shrink-0">
              <SelectValue placeholder="Source" />
            </SelectTrigger>
            <SelectContent>
              {LOG_SOURCES.map((s) => (
                <SelectItem key={s.value} value={s.value}>
                  {s.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {source === 'edge-functions' && deployedFunctions.length > 0 && (
            <Select value={selectedFunction} onValueChange={setSelectedFunction}>
              <SelectTrigger className="h-8 text-xs w-auto min-w-fit px-2.5 flex-shrink-0 max-w-32">
                <SelectValue placeholder="All" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All functions</SelectItem>
                {deployedFunctions.map((fn) => (
                  <SelectItem key={fn.id} value={fn.slug}>
                    {fn.slug}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          {source === 'edge-functions' && (
            <Select value={edgeFunctionView} onValueChange={(v) => setEdgeFunctionView(v as EdgeFunctionView)}>
              <SelectTrigger className="h-8 text-xs w-auto min-w-fit px-2.5 flex-shrink-0">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="invocations">Invocations</SelectItem>
                <SelectItem value="console">Logs</SelectItem>
              </SelectContent>
            </Select>
          )}

          {source !== 'postgres' && (
            <Select value={interval} onValueChange={(v) => setInterval(v as TimeInterval)}>
              <SelectTrigger className="h-8 text-xs w-auto min-w-fit px-2.5 flex-shrink-0">
                <SelectValue placeholder="Time" />
              </SelectTrigger>
              <SelectContent>
                {TIME_INTERVALS.map((t) => (
                  <SelectItem key={t.value} value={t.value}>
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-3">
          <Select value={source} onValueChange={(v) => handleSourceChange(v as LogSource)}>
            <SelectTrigger className="h-9 w-40">
              <SelectValue placeholder="Source" />
            </SelectTrigger>
            <SelectContent>
              {LOG_SOURCES.map((s) => (
                <SelectItem key={s.value} value={s.value}>
                  {s.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {source === 'edge-functions' && deployedFunctions.length > 0 && (
            <Select value={selectedFunction} onValueChange={setSelectedFunction}>
              <SelectTrigger className="h-9 w-44">
                <SelectValue placeholder="All functions" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All functions</SelectItem>
                {deployedFunctions.map((fn) => (
                  <SelectItem key={fn.id} value={fn.slug}>
                    {fn.slug}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          {source === 'edge-functions' && (
            <Select value={edgeFunctionView} onValueChange={(v) => setEdgeFunctionView(v as EdgeFunctionView)}>
              <SelectTrigger className="h-9 w-36">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="invocations">Invocations</SelectItem>
                <SelectItem value="console">Logs</SelectItem>
              </SelectContent>
            </Select>
          )}

          {source !== 'postgres' && (
            <Select value={interval} onValueChange={(v) => setInterval(v as TimeInterval)}>
              <SelectTrigger className="h-9 w-40">
                <SelectValue placeholder="Time range" />
              </SelectTrigger>
              <SelectContent>
                {TIME_INTERVALS.map((t) => (
                  <SelectItem key={t.value} value={t.value}>
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      )}

      {/* Logs/Invocations list */}
      {loading ? (
        <div className="flex items-center justify-center h-48">
          <div className="flex items-center gap-2 text-muted-foreground">
            <div className="w-4 h-4 border-2 border-muted-foreground/30 border-t-muted-foreground rounded-full animate-spin" />
            <span>Loading {edgeFunctionView === 'invocations' ? 'invocations' : 'logs'}...</span>
          </div>
        </div>
      ) : source === 'edge-functions' && edgeFunctionView === 'invocations' ? (
        // Invocations view
        invocations.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border/60 p-8">
            <div className="flex flex-col items-center justify-center text-center">
              <div className="p-3 rounded-full bg-muted/50 mb-3">
                <Terminal className="w-5 h-5 text-muted-foreground" />
              </div>
              <p className="text-sm font-medium mb-1">No invocations found</p>
              <p className="text-sm text-muted-foreground">
                No function invocations in this time range
              </p>
            </div>
          </div>
        ) : (
          <div className="rounded-lg border border-border/60 overflow-hidden">
            <div className="divide-y divide-border/40">
              {invocations.map((inv, index) => {
                const showDate = index === 0 ||
                  formatDate(inv.timestamp) !== formatDate(invocations[index - 1].timestamp);
                const isExpanded = expandedLogs.has(inv.id);

                return (
                  <div key={inv.id}>
                    {showDate && (
                      <div className="px-4 py-2 bg-muted/30 text-xs font-medium text-muted-foreground">
                        {formatDate(inv.timestamp)}
                      </div>
                    )}
                    <div
                      className="px-4 py-3 hover:bg-muted/20 transition-colors cursor-pointer"
                      onClick={() => toggleLogExpanded(inv.id)}
                    >
                      <div className="flex items-center gap-4">
                        <div className="flex-shrink-0">
                          {isExpanded ? (
                            <ChevronDown className="w-4 h-4 text-muted-foreground" />
                          ) : (
                            <ChevronRight className="w-4 h-4 text-muted-foreground" />
                          )}
                        </div>
                        <span className="text-xs font-mono text-muted-foreground w-20 flex-shrink-0">
                          {formatTimestamp(inv.timestamp)}
                        </span>
                        <span className="text-xs font-medium px-1.5 py-0.5 rounded bg-muted text-muted-foreground flex-shrink-0 w-14 text-center">
                          {inv.method}
                        </span>
                        <span className={`text-xs font-medium px-1.5 py-0.5 rounded flex-shrink-0 w-12 text-center ${
                          inv.status >= 500 ? 'bg-red-500/10 text-red-600' :
                          inv.status >= 400 ? 'bg-amber-500/10 text-amber-600' :
                          'bg-green-500/10 text-green-600'
                        }`}>
                          {inv.status}
                        </span>
                        <span className="text-sm font-mono text-muted-foreground flex-1 w-0 truncate" title={inv.path}>
                          {inv.path}
                        </span>
                      </div>
                      {isExpanded && (() => {
                        const meta = Array.isArray(inv.metadata) ? inv.metadata[0] : inv.metadata;
                        const logData = {
                          function: inv.functionName,
                          method: inv.method,
                          status: inv.status,
                          path: inv.path,
                          duration: `${inv.duration}ms`,
                          user_agent: meta?.request?.[0]?.headers?.[0]?.user_agent || '',
                          x_client_info: meta?.request?.[0]?.headers?.[0]?.x_client_info || '',
                          region: meta?.response?.[0]?.headers?.[0]?.x_sb_edge_region || '',
                        };
                        const jsonStr = JSON.stringify(logData, null, 2);
                        return (
                          <div className="mt-3 ml-8 rounded-md bg-muted/30 border border-border/40 overflow-hidden">
                            <div className="flex items-center justify-between px-3 py-2 border-b border-border/40">
                              <span className="text-xs font-medium text-muted-foreground">Raw log data</span>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigator.clipboard.writeText(jsonStr);
                                  toast.success('Copied to clipboard');
                                }}
                                className="flex items-center gap-1 px-2 py-1 text-xs text-muted-foreground hover:text-foreground hover:bg-muted rounded transition-colors"
                              >
                                <Copy className="w-3 h-3" />
                                Copy JSON
                              </button>
                            </div>
                            <pre className="p-3 text-xs font-mono text-muted-foreground overflow-x-auto max-h-64 overflow-y-auto whitespace-pre">
                              {jsonStr}
                            </pre>
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )
      ) : logs.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border/60 p-8">
          <div className="flex flex-col items-center justify-center text-center">
            <div className="p-3 rounded-full bg-muted/50 mb-3">
              <Terminal className="w-5 h-5 text-muted-foreground" />
            </div>
            <p className="text-sm font-medium mb-1">No logs found</p>
            <p className="text-sm text-muted-foreground">
              No logs match your current filters
            </p>
          </div>
        </div>
      ) : (
        <div className="rounded-lg border border-border/60 overflow-hidden min-w-0">
          <div className="divide-y divide-border/40 min-w-0">
            {logs.map((log, index) => {
              // Show date separator if different from previous log
              const showDate = index === 0 ||
                formatDate(log.timestamp) !== formatDate(logs[index - 1].timestamp);
              const isExpanded = expandedLogs.has(log.id);

              // Parse API Gateway / Storage pipe-delimited logs
              // API: METHOD | STATUS | IP | REQ_ID | URL | UA
              // Storage: REF | METHOD | STATUS | IP | REQ_ID | PATH | UA
              const apiParsed = (log.source === 'api' || log.source === 'storage') ? (() => {
                const parts = log.message.split(' | ');
                if (parts.length < 3) return null;
                if (log.source === 'storage') {
                  const method = parts[1]?.trim() || '';
                  const status = parseInt(parts[2]?.trim() || '0');
                  const path = parts[5]?.trim() || '';
                  return { method, status, path };
                }
                const method = parts[0]?.trim() || '';
                const status = parseInt(parts[1]?.trim() || '0');
                let path = '';
                try { path = new URL(parts[4]?.trim() || '').pathname; } catch { path = parts[4]?.trim() || ''; }
                return { method, status, path };
              })() : null;

              // Parse auth JSON event_message for display
              const authParsed = log.source === 'auth' ? (() => {
                try {
                  const parsed = JSON.parse(log.message);
                  if (parsed.method && parsed.path) return { method: parsed.method as string, path: parsed.path as string, msg: null as string | null };
                  if (parsed.msg) return { method: null as string | null, path: null as string | null, msg: parsed.msg as string };
                } catch { /* not JSON */ }
                return null;
              })() : null;

              return (
                <div key={log.id}>
                  {showDate && (
                    <div className="px-4 py-2 bg-muted/30 text-xs font-medium text-muted-foreground">
                      {formatDate(log.timestamp)}
                    </div>
                  )}
                  <div
                    className={cn(
                      "hover:bg-muted/20 transition-colors cursor-pointer overflow-hidden",
                      isMobile ? "px-3 py-2" : "px-4 py-3"
                    )}
                    onClick={() => toggleLogExpanded(log.id)}
                  >
                    {apiParsed ? (
                      // API Gateway layout: method badge + colored status + path
                      <div className="flex items-center gap-4">
                        <div className="flex-shrink-0">
                          {isExpanded ? (
                            <ChevronDown className="w-4 h-4 text-muted-foreground" />
                          ) : (
                            <ChevronRight className="w-4 h-4 text-muted-foreground" />
                          )}
                        </div>
                        <span className="text-xs font-mono text-muted-foreground w-20 flex-shrink-0">
                          {formatTimestamp(log.timestamp)}
                        </span>
                        <span className="text-xs font-medium px-1.5 py-0.5 rounded bg-muted text-muted-foreground flex-shrink-0 w-14 text-center">
                          {apiParsed.method}
                        </span>
                        <span className={`text-xs font-medium px-1.5 py-0.5 rounded flex-shrink-0 w-12 text-center ${
                          apiParsed.status >= 500 ? 'bg-red-500/10 text-red-600' :
                          apiParsed.status >= 400 ? 'bg-amber-500/10 text-amber-600' :
                          'bg-green-500/10 text-green-600'
                        }`}>
                          {apiParsed.status}
                        </span>
                        <span className="text-sm font-mono text-muted-foreground flex-1 w-0 truncate" title={apiParsed.path}>
                          {apiParsed.path}
                        </span>
                      </div>
                    ) : authParsed ? (
                      // Auth layout: time + level badge + method/path or msg
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="flex-shrink-0">
                          {isExpanded ? (
                            <ChevronDown className="w-4 h-4 text-muted-foreground" />
                          ) : (
                            <ChevronRight className="w-4 h-4 text-muted-foreground" />
                          )}
                        </div>
                        <span className="text-xs font-mono text-muted-foreground flex-shrink-0 w-20">
                          {formatTimestamp(log.timestamp)}
                        </span>
                        <span className={`px-1.5 py-0.5 text-xs font-medium rounded flex-shrink-0 ${getLevelBadgeClass(log.level)}`}>
                          {log.level.toUpperCase()}
                        </span>
                        {authParsed.method ? (
                          <>
                            <span className="text-xs font-medium px-1.5 py-0.5 rounded bg-muted text-muted-foreground flex-shrink-0">
                              {authParsed.method}
                            </span>
                            <span className="text-sm font-mono text-muted-foreground flex-1 w-0 truncate" title={authParsed.path || ''}>
                              {authParsed.path}
                            </span>
                          </>
                        ) : (
                          <span className="text-sm font-mono flex-1 w-0 truncate" title={authParsed.msg || ''}>
                            {authParsed.msg}
                          </span>
                        )}
                      </div>
                    ) : isMobile ? (
                      // Mobile layout: stacked
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <div className="flex-shrink-0">
                            {isExpanded ? (
                              <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />
                            ) : (
                              <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />
                            )}
                          </div>
                          <span className={`px-1.5 py-0.5 text-xs font-medium rounded flex-shrink-0 ${getLevelBadgeClass(log.level)}`}>
                            {log.level.toUpperCase()}
                          </span>
                          <span className="text-xs font-mono text-muted-foreground">
                            {formatTimestamp(log.timestamp)}
                          </span>
                          {log.functionName && (
                            <span className="text-xs font-mono text-muted-foreground truncate max-w-24">
                              {log.functionName}
                            </span>
                          )}
                          <button
                            onClick={(e) => copyLog(log, e)}
                            className="p-1 hover:bg-muted rounded text-muted-foreground hover:text-foreground transition-colors flex-shrink-0 ml-auto"
                            title="Copy log"
                          >
                            <Copy className="w-3 h-3" />
                          </button>
                        </div>
                        <p className={`text-xs font-mono ml-5 ${isExpanded ? 'break-all' : 'truncate'}`}>
                          {log.message}
                        </p>
                      </div>
                    ) : (
                      // Desktop layout: single row
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="flex-shrink-0">
                          {isExpanded ? (
                            <ChevronDown className="w-4 h-4 text-muted-foreground" />
                          ) : (
                            <ChevronRight className="w-4 h-4 text-muted-foreground" />
                          )}
                        </div>
                        <span className="text-xs font-mono text-muted-foreground flex-shrink-0 w-20">
                          {formatTimestamp(log.timestamp)}
                        </span>
                        <span className={`px-1.5 py-0.5 text-xs font-medium rounded flex-shrink-0 ${getLevelBadgeClass(log.level)}`}>
                          {log.level.toUpperCase()}
                        </span>
                        {log.functionName && (
                          <span
                            className="text-xs font-mono px-1.5 py-0.5 rounded bg-muted text-muted-foreground flex-shrink-0 max-w-32 truncate"
                            title={log.functionName}
                          >
                            {log.functionName}
                          </span>
                        )}
                        <p className={`text-sm font-mono flex-1 w-0 ${isExpanded ? 'break-all' : 'truncate'}`}>
                          {log.message}
                        </p>
                        <button
                          onClick={(e) => copyLog(log, e)}
                          className="p-1.5 hover:bg-muted rounded text-muted-foreground hover:text-foreground transition-colors flex-shrink-0"
                          title="Copy log"
                        >
                          <Copy className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    )}
                    {isExpanded && (() => {
                      let logData: Record<string, unknown>;
                      if (apiParsed) {
                        const parts = log.message.split(' | ');
                        const userAgent = log.source === 'storage' ? parts[6]?.trim() || '' : parts[5]?.trim() || '';
                        logData = {
                          id: log.id,
                          status: apiParsed.status,
                          method: apiParsed.method,
                          path: apiParsed.path,
                          user_agent: userAgent,
                          event_message: log.message,
                        };
                      } else if (log.source === 'auth') {
                        // Show id + formatted event_message
                        let formattedMessage: unknown = log.message;
                        try { formattedMessage = JSON.parse(log.message); } catch { /* keep raw */ }
                        logData = { id: log.id, event_message: formattedMessage };
                      } else {
                        logData = {
                          id: log.id,
                          timestamp: log.timestamp.toISOString(),
                          level: log.level,
                          ...(log.functionName ? { function: log.functionName } : {}),
                          event_message: log.message,
                        };
                      }
                      const jsonStr = JSON.stringify(logData, null, 2);
                      return (
                        <div className="mt-3 ml-7 rounded-md bg-muted/30 border border-border/40 overflow-hidden">
                          <div className="flex items-center justify-between px-3 py-2 border-b border-border/40">
                            <span className="text-xs font-medium text-muted-foreground">Raw log data</span>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                navigator.clipboard.writeText(jsonStr);
                                toast.success('Copied to clipboard');
                              }}
                              className="flex items-center gap-1 px-2 py-1 text-xs text-muted-foreground hover:text-foreground hover:bg-muted rounded transition-colors"
                            >
                              <Copy className="w-3 h-3" />
                              Copy JSON
                            </button>
                          </div>
                          <pre className="p-3 text-xs font-mono text-muted-foreground overflow-x-auto max-h-64 overflow-y-auto whitespace-pre">
                            {jsonStr}
                          </pre>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
